import { type VariantProps, cva } from "class-variance-authority";
import type { ToastRootProps } from "reka-ui";
import type { HTMLAttributes } from "vue";

export const alertVariants = cva("relative rounded-lg border p-4", {
    variants: {
        variant: {
            default: "bg-white text-gray-950",
            danger: "text-danger border-danger",
            warning: "text-warning-700 border-warning bg-warning-50",
            success: "text-success border-success",
        },
    },
    defaultVariants: {
        variant: "default",
    },
});

export type AlertVariants = VariantProps<typeof alertVariants>;

export const badgeVariants = cva(
    "inline-flex space-x-2 items-center whitespace-nowrap rounded-md px-2.5 py-0.5 transition-colors select-none focus:outline-hidden focus:ring-2 focus:ring-gray-950 focus:ring-offset-2",
    {
        variants: {
            variant: {
                default: "",
                primary: "bg-primary",
                accent: "bg-accent",
                success: "bg-success",
                warning: "bg-warning",
                danger: "bg-danger",
                outline: "border",
                highlight: "bg-accent-highlight border",
            },
            fill: {
                light: "text-gray-950",
                dark: "text-white",
            },
        },
        compoundVariants: [
            { variant: "default", fill: "light", class: "text-gray-900 bg-gray-200" },
            { variant: "default", fill: "dark", class: "bg-gray-500" },
            { variant: "primary", fill: "light", class: "bg-primary-100 text-primary-800" },
            { variant: "primary", fill: "dark", class: "bg-primary-600 text-primary-50" },
            { variant: "accent", fill: "light", class: "bg-accent-secondary" },
            { variant: "outline", fill: "light", class: "text-gray-600" },
            { variant: "outline", fill: "dark", class: "text-gray-950" },
            { variant: "success", fill: "light", class: "bg-success-200 text-success-900" },
            { variant: "warning", fill: "light", class: "bg-warning-200 text-warning-900" },
            { variant: "warning", fill: "dark", class: "bg-warning text-gray-950" },
            { variant: "danger", fill: "light", class: "bg-danger-200 text-danger-900" },
            { variant: "highlight", fill: "light", class: "text-gray-600 bg-accent-highlight" },
            { variant: "highlight", fill: "dark", class: "bg-accent-highlight text-gray-950  border-gray-400" },
        ],
        defaultVariants: {
            variant: "default",
            fill: "dark",
        },
    }
);

export type BadgeVariants = VariantProps<typeof badgeVariants>;

export const buttonVariants = cva(
    "inline-flex space-x-2 shrink-0 font-medium items-center justify-center whitespace-nowrap ring-offset-white transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "",
                accent: "text-accent",
                primary: "text-primary",
                success: "text-success-700",
                danger: "text-danger-700",
                warning: "text-warning-700",
                link: "text-link bg-transparent hover:text-link-hover hover:underline",
                white: "bg-white text-gray-900 border-0 hover:text-gray-900 hover:bg-gray-100",
            },
            size: {
                xs: "h-6 text-xs rounded-xs p-2",
                sm: "h-8 text-xs rounded-sm px-4",
                md: "h-10 text-sm px-4 rounded-sm py-1",
                lg: "h-11 text-base rounded-md px-4",
                xl: "h-14 text-lg rounded-md px-6",
                icon: "size-8 rounded-sm text-sm text-center",
            },
            fill: {
                none: "",
                outline: "border",
                light: "bg-gray-100 hover:bg-gray-200",
                solid: "text-white hover:text-white",
            },
        },
        compoundVariants: [
            { variant: "default", fill: "outline", class: "border hover:bg-gray-100" },
            {
                variant: "default",
                fill: "solid",
                class: "text-gray-900 bg-gray-300 hover:bg-gray-200 hover:text-gray-900",
            },
            { variant: "accent", fill: "outline", class: "border-accent hover:bg-gray-100" },
            { variant: "accent", fill: "solid", class: "bg-accent" },
            { variant: "accent", fill: "light", class: "bg-accent-secondary" },
            { variant: "primary", fill: "outline", class: "border-primary hover:bg-primary-100 " },
            { variant: "primary", fill: "solid", class: "bg-primary hover:bg-primary-700" },
            { variant: "primary", fill: "light", class: "bg-primary-100 hover:bg-primary-200" },
            { variant: "success", fill: "outline", class: "border-success hover:bg-success-50" },
            { variant: "success", fill: "solid", class: "bg-success hover:bg-success-700" },
            { variant: "success", fill: "light", class: "bg-success-100 hover:bg-success-200" },
            { variant: "warning", fill: "outline", class: "border-warning hover:bg-warning-50" },
            {
                variant: "warning",
                fill: "solid",
                class: "text-warning-950 bg-warning hover:bg-warning-600",
            },
            {
                variant: "warning",
                fill: "light",
                class: "text-warning-950 bg-warning-100 hover:bg-warning-200",
            },
            { variant: "danger", fill: "outline", class: "border-danger hover:bg-danger-50" },
            { variant: "danger", fill: "solid", class: "bg-danger hover:bg-danger-700" },
            { variant: "danger", fill: "light", class: "bg-danger-100 hover:bg-danger-200" },
            { variant: "link", fill: "outline", class: "border-0" },
            { variant: "link", fill: "solid", class: "text-link hover:text-link-hover" },
            { variant: "white", fill: "outline", class: "border-0" },
            { variant: "white", fill: "solid", class: "text-gray-900 hover:text-gray-900" },
            { variant: "white", fill: "light", class: "bg-white" },
        ],
        defaultVariants: {
            variant: "default",
            size: "md",
            fill: "outline",
        },
    }
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

export const spinnerVariants = cva("inline-block animate-spin will-change-transform", {
    variants: {
        variant: {
            primary: "text-primary",
            accent: "text-accent",
            success: "text-success",
            danger: "text-danger",
            warning: "text-warning",
            white: "text-white",
            black: "text-black",
        },
        size: {
            xs: "h-6 w-6",
            sm: "h-8 w-8",
            md: "h-10 w-10",
            lg: "h-12 w-12",
            xl: "h-20 w-20",
        },
    },
    defaultVariants: {
        variant: "primary",
        size: "md",
    },
});

export type SpinnerVariants = VariantProps<typeof spinnerVariants>;

export const toastVariants = cva(
    "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-(--radix-toast-swipe-end-x) data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x) data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full dark:border-gray-800",
    {
        variants: {
            variant: {
                default: "border bg-gray-100 text-gray-950",
                danger: "border-danger-500 bg-danger-200",
                warning: "border-warning-300 bg-warning-200",
                success: "border-success-500 bg-success-200",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
);

export type ToastVariants = VariantProps<typeof toastVariants>;

export interface ToastProps extends ToastRootProps {
    class?: HTMLAttributes["class"];
    variant?: ToastVariants["variant"];
    onOpenChange?: ((value: boolean) => void) | undefined;
}

// Shared "true tab" styling: a lightweight underline row rather than a heavy segmented control.
// Used by both the generic Tabs components and the Search tabs so they stay visually consistent.
// The scroll container, baseline border and overflow arrows live in TabsScrollArea, so this is
// just the inner tab row: it grows to its content (w-max) but never narrower than the viewport.
export const tabsListClass = "flex w-max min-w-full";

export const tabsTriggerClass =
    "relative inline-flex shrink-0 snap-start items-center justify-center gap-2 whitespace-nowrap -mb-px border-b-2 border-transparent px-4 py-2.5 text-gray-600 ring-offset-white transition-colors hover:text-accent hover:border-gray-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-accent data-[state=active]:text-accent data-[state=active]:font-semibold";
